import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Tooltip } from '@mui/material';

import { Lock, PenLine } from 'lucide-react';

import { Project } from '../../types/project';
import { GEOMETRY_LOCK_MESSAGE, lockingVersion, projectGeometryLock } from './projectGeometry';

/**
 * Opens the drawing a project was created from, to change the project's geometry: a small card
 * with the plan's picture so it reads as "this project's drawing". After a calculation the geometry
 * is locked for every version (they share one model), so the card explains that and offers the way
 * on — a new project from the drawing. While luminaires are being positioned it is just disabled.
 */
export function EditDrawingButton({ project }: { project: Project }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [explaining, setExplaining] = useState(false);
  const lock = projectGeometryLock(project);

  const open = () => {
    if (lock === 'calculation') setExplaining(true);
    else navigate(`/drawings/${project.sourceDrawingId}?project=${project.id}`);
  };

  return (
    <>
      <Tooltip title={lock ? t(GEOMETRY_LOCK_MESSAGE[lock]) : t('drawings.editDrawingHint')} placement="bottom-start">
        {/* A disabled button fires no hover events, so the tooltip listens on this wrapper. */}
        <span className="absolute top-4 left-[22px] z-40">
          <button
            onClick={open}
            disabled={lock === 'autopositioning'}
            className={`group flex items-center gap-2.5 h-11 pl-1 pr-4 rounded-xl bg-[#1E1E1E] border text-left shadow-2xl transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              lock
                ? 'border-white/10 hover:border-white/30'
                : 'border-[#B3EC49]/40 hover:border-[#B3EC49] hover:bg-[#232a18]'
            }`}
          >
            <span className="relative h-9 w-9 shrink-0 overflow-hidden rounded-lg border border-white/10 bg-black">
              {project.previewImageUrl && (
                <img
                  src={project.previewImageUrl}
                  alt=""
                  className={`h-full w-full object-cover ${lock ? 'opacity-40' : ''}`}
                />
              )}
              {lock && <Lock className="absolute inset-0 m-auto h-3.5 w-3.5 text-white/80" />}
            </span>
            <span className="flex flex-col leading-tight">
              <span
                className={`flex items-center gap-1.5 text-xs font-semibold ${lock ? 'text-gray-300' : 'text-white group-hover:text-[#B3EC49]'}`}
              >
                {!lock && <PenLine className="h-3.5 w-3.5 text-[#B3EC49]" />}
                {t('drawings.editDrawing')}
              </span>
              <span className="text-[11px] text-gray-500">
                {lock ? t(`drawings.editDrawingLocked.${lock}`) : t('drawings.editDrawingSub')}
              </span>
            </span>
          </button>
        </span>
      </Tooltip>

      {explaining && (
        <div
          className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/60"
          onClick={() => setExplaining(false)}
        >
          <div
            role="dialog"
            aria-labelledby="drawing-locked-title"
            className="w-[460px] max-w-[calc(100vw-32px)] rounded-2xl bg-[#232323] border border-white/10 shadow-2xl p-6 text-sm text-gray-300"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="drawing-locked-title" className="flex items-center gap-2 text-base font-semibold text-white mb-4">
              <Lock className="h-4 w-4 text-orange-300" />
              {t('drawings.lockedDialog.title')}
            </h2>
            <div className="space-y-3 leading-relaxed">
              <p>{t('drawings.lockedDialog.shared')}</p>
              <p className="text-orange-200">
                {t('drawings.lockedDialog.calculated', { version: lockingVersion(project)?.name ?? '' })}
              </p>
              <p>{t('drawings.lockedDialog.newProject')}</p>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setExplaining(false)}
                className="h-9 px-4 rounded-full text-xs font-medium border border-white/20 text-gray-200 hover:border-white/40"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => navigate(`/drawings/${project.sourceDrawingId}`)}
                className="h-9 px-4 rounded-full text-xs font-medium bg-lime-400 text-black hover:bg-lime-500"
              >
                {t('drawings.lockedDialog.open')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
